import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';

import TransitionsModal from '../../modals/TransitionsModal';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import f1Grapgh from '../../../assets/calculations/stroud/f1.JPG';
import f2Grapgh from '../../../assets/calculations/stroud/f2.JPG';
import SeoHelmet from '../../common/SeoHelmet';

const StroudTheory = () => {
  return (
    <>
      <SeoHelmet
        title="GeoCalc | cu–mv & SPT–PI Relationships — Stroud (1974) | Theory"
        description="Summary of Stroud (1974) correlations linking SPT N60 to undrained shear strength (cu) and volumetric compressibility (mv) via PI-dependent factors f1 and f2."
        url="https://www.geocalc.app/stroud-theory"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="page"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Stroud (1974) | Theory', url: 'https://www.geocalc.app/stroud-theory' },
        ]}
      />

      <Header />
      <div className="theory">
        <div className="theoryContainer">
          <h1>
            c<sub>u</sub> - m<sub>v</sub> & SPT - PI Relationships | Stroud (1974) | Theory & Formulations
          </h1>
          <p>
            Database comprised of 1200 SPT-N values from 42 different sites in UK by Stroud (1974). In this study, unconsolidated
            undrained (UU) triaxial tests were performed on high quality undisturbed samples in order to obtain actual undrained
            shear strength values of clayey soils.
          </p>
          <p>
            As a result, Stroud (1974) reported that for clayey soils with plasticity index (PI) in range of 25 - 60 and SPT-N
            values in range of 10 - 60 blow/30 cm, undrained shear strength values obtained from UU tests are in range of 25 - 500
            kN/m<sup>2</sup>. Stroud (1974) proposed a correlation between f<sub>1</sub> and PI as shown in Figure A where f
            <sub>1</sub> is a factor depending on plasticity index.
          </p>
          <TransitionsModal
            image={f1Grapgh}
            title="Stroud (1974)"
            description={'Relation between SPT-N60 - cu - PI'}
            buttonText={'Figure A'}
          ></TransitionsModal>
          <p className="theoryEquation">
            <math display="block">
              {' '}
              <mrow>
                {' '}
                <msub>
                  {' '}
                  <mi>c</mi> <mi>u</mi>{' '}
                </msub>{' '}
                <mo>(</mo> <mtext>kPa</mtext> <mo>)</mo> <mo>=</mo>{' '}
                <msub>
                  {' '}
                  <mi>f</mi> <mn>1</mn>{' '}
                </msub>{' '}
                <mo>&#215;</mo>{' '}
                <msub>
                  {' '}
                  <mi>N</mi> <mn>60</mn>{' '}
                </msub>{' '}
              </mrow>{' '}
            </math>
          </p>

          <p>
            Based on the previously stated study, Stroud (1974) proposed a correlation between f<sub>2</sub> and PI for the
            relation between coefficient of volumetric compression (m<sub>v</sub>) and SPT-N value as shown in Figure B. Here, f
            <sub>2</sub> is a factor depending on plasticity index.
          </p>
          <TransitionsModal
            image={f2Grapgh}
            title="Stroud (1974)"
            description={'Relation between SPT-N60 - mv - PI'}
            buttonText={'Figure B'}
          ></TransitionsModal>
          <p className="theoryEquation">
            <math display="block">
              {' '}
              <mrow>
                {' '}
                <msub>
                  {' '}
                  <mi>m</mi> <mi>v</mi>{' '}
                </msub>{' '}
                <mo>(</mo>{' '}
                <mfrac>
                  {' '}
                  <msup>
                    {' '}
                    <mi>m</mi> <mn>2</mn>{' '}
                  </msup>{' '}
                  <mrow>
                    {' '}
                    <mi>kN</mi>{' '}
                  </mrow>{' '}
                </mfrac>{' '}
                <mo>)</mo> <mo>=</mo>{' '}
                <mfrac>
                  {' '}
                  <mn>1</mn>{' '}
                  <mrow>
                    {' '}
                    <msub>
                      {' '}
                      <mi>f</mi> <mn>2</mn>{' '}
                    </msub>{' '}
                    <mo>&#215;</mo>{' '}
                    <msub>
                      {' '}
                      <mi>N</mi> <mn>60</mn>{' '}
                    </msub>{' '}
                  </mrow>{' '}
                </mfrac>{' '}
              </mrow>{' '}
            </math>
          </p>

          <div className="theoryButtonContainer">
            <Link to={'/calculations/stroud'}>
              <Button variant="outlined" size="small">
                GO TO cu & mv CALCULATOR
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default StroudTheory;
